/**
 * Contract for POST /api/v1/imagegen/visiting-card — an AI-generated business
 * visiting-card face: logo, name/phones/address/email, and a reserved QR
 * corner, sized for print (3.5x2in). Same /api/v1/imagegen controller family
 * as Poster and image-revamp — see PosterObject.ts for the sibling contract
 * this mirrors move-for-move.
 *
 * Curator-gated (JWT), same as Poster. Deliberately self-contained: unlike
 * Poster (which resolves the business's own details server-side), every
 * field here is taken exactly as supplied — no businessId, no server-side
 * lookup. This file duplicates rather than imports Poster's types (matching
 * this codebase's own convention — ImageRevampObject.ts and PosterObject.ts
 * each keep their own independent copies of structurally similar shapes) so
 * the two endpoints' contracts can never drift into each other by accident.
 */

// ── Stored card details ──

/** Max phone numbers a card carries. Matches VisitingCardService's own
 *  MAX_PHONE_NUMBERS (e.g. a mobile, a WhatsApp line and an office landline);
 *  the prompt's "shrink the contact lines" fitting is sized for three, so the
 *  UI hides "+ Add another" at the limit rather than build a rejected request. */
export const VISITING_CARD_MAX_PHONES = 3;

/** Max characters of the card address — the longest line the card fits. */
export const VISITING_CARD_MAX_ADDRESS_CHARS = 200;

/** Key of the business attribute (moreDetails) the approved draft's card lands in. */
export const VISITING_CARD_ATTRIBUTE_KEY = "VISITING_CARD";

/**
 * What gets printed on the business's visiting card, captured when the draft
 * business is created. Phones and address are mandatory; email is optional.
 * Sent inside businessPayload (payloadVersion 2), stored on the business under
 * VISITING_CARD_ATTRIBUTE_KEY on approval, and read back through
 * backendToUi/visitingCardConvertor (BusinessProfile.visitingCard).
 */
export interface VisitingCardDetails {
  phones: string[];
  address: string;
  email?: string;
}

// ── Request ──

/**
 * What each referenced image contributes to the prompt — same role
 * vocabulary as Poster's own PosterImageRole, but a card only ever sends
 * LOGO and REFERENCE (PRODUCT is rejected server-side: a card has no
 * product collage to feature one in).
 *  - LOGO       the business's own logo/brand mark. REQUIRED — exactly one.
 *               The client itself already blocks generation when no logo is
 *               resolvable, but the backend validates too.
 *  - REFERENCE  a style/mood reference — NOT rendered verbatim, just informs
 *               look and feel (matches Poster's own REFERENCE-style input).
 *               1 to 3 of these.
 */
export type VisitingCardImageRole = "LOGO" | "REFERENCE";

/** Pointer to an image ALREADY uploaded to Supabase — bucket-relative path,
 *  never raw bytes. Same convention as Poster's objectPath fields. */
export interface VisitingCardImageInput {
  objectPath: string;
  role: VisitingCardImageRole;
}

/**
 * How the card should look. Identical shape to Poster's own style object —
 * every field optional, each present one becomes one line of the prompt.
 */
export interface VisitingCardStyle {
  /** Hex color, e.g. "#7B2D26". */
  baseBackgroundColor?: string;
  /** Accent colors — guidance, not a guarantee. */
  palette?: string[];
  /** Free string, e.g. "BAKERY" — steers imagery and tone. */
  listingType?: string;
  /** Free-text creative direction. */
  instructions?: string;
}

export interface VisitingCardRequest {
  /** Rendered as the card's headline line. Required — the only required
   *  field in this contract. */
  name: string;
  /** Zero to three phone numbers, as plain strings rendered exactly as
   *  given — no label, no structure. Replaces an earlier single
   *  `phone: string` field with a list; a brief labelled-entry shape was
   *  tried in between and then deliberately dropped (labels weren't
   *  needed). Validated server-side too (VisitingCardService). */
  phones?: string[];
  address?: string;
  email?: string;
  /** Every input image, each labelled with a role — see
   *  VisitingCardImageRole. Required: exactly one LOGO and one to three
   *  REFERENCE. Replaces an earlier shape with a separate `logo` field and
   *  `referenceImages` list; that shape asked the model to infer which
   *  attached image was the logo purely from what it looked like, and a real
   *  generation showed it inferring wrong. A flat role-tagged list — the
   *  same contract Poster already uses — removes that ambiguity instead of
   *  re-wording around it. */
  images: VisitingCardImageInput[];
  style?: VisitingCardStyle;
}

// ── Response ──

export interface VisitingCardImage {
  /** Always JPEG — render with `data:image/jpeg;base64,` + base64. */
  base64: string;
  width: number;
  height: number;
}

/** A pixel rect in the SAME coordinate space as `image.width`/`image.height`
 *  — draw straight into these coordinates at the card's native resolution. */
export interface VisitingCardRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface VisitingCardContent {
  /** Written to a server-side audit row — quote it in bug reports. */
  generationId: string;
  image: VisitingCardImage;
  /** The AI's own suggested QR rect — corner-anchored (top-right inset),
   *  unlike Poster's horizontally-centred one. Kept as a fallback; the
   *  client sizes the drawn QR to `reservedZone` instead, same as Poster. */
  qrArea: VisitingCardRect;
  /** `qrArea` inflated by an equal margin on every side — derive the margin
   *  as `(reservedZone.width - qrArea.width) / 2` rather than hardcoding a
   *  pixel value, same caution as Poster's own contract. */
  reservedZone: VisitingCardRect;
  aiGenerated: boolean;
  promptVersion: string;
  createdAt: string;
}

export interface VisitingCardGeneralProcessingStatus {
  processingStatus: "OK" | "ERROR";
  processingDetails: string;
}

export interface VisitingCardErrorInformation {
  errorCode: string;
  errorDetail: string;
  additionalDetails: Record<string, any> | null;
}

export interface VisitingCardResponse {
  /** null on success, populated (never empty) on failure. */
  errorInformation: VisitingCardErrorInformation[] | null;
  generalProcessingStatus: VisitingCardGeneralProcessingStatus;
  /** null on failure. Exactly one entry on success. */
  contents: VisitingCardContent[] | null;
}

// ── Error codes ──

/** Same /api/v1/imagegen auth/validation middleware as Poster — see
 *  VisitingCardController's own httpStatusFor mapping (confirmed against
 *  backend source, not guessed the way Poster's own comment once was). */
export const VISITING_CARD_ERROR_CODES = {
  VALIDATION_FAILED: "AI_IMG_VALIDATION_FAILED",
  UNSUPPORTED_FORMAT: "AI_IMG_UNSUPPORTED_FORMAT",
  MODERATION_BLOCKED: "AI_IMG_MODERATION_BLOCKED",
  UNAUTHENTICATED: "BULK_CREATE_UNAUTHENTICATED",
  NOT_CURATOR: "BULK_CREATE_NOT_CURATOR",
  QUOTA_EXCEEDED: "AI_IMG_QUOTA_EXCEEDED",
  BUSY: "AI_IMG_BUSY",
  PROVIDER_FAILED: "AI_IMG_PROVIDER_FAILED",
  BUDGET_EXCEEDED: "AI_IMG_BUDGET_EXCEEDED",
} as const;

export const VISITING_CARD_ERROR_COPY: Record<
  string,
  { message: string; retryable: boolean }
> = {
  [VISITING_CARD_ERROR_CODES.VALIDATION_FAILED]: {
    message: "That request was missing something the AI tool needs. Please try again.",
    retryable: true,
  },
  [VISITING_CARD_ERROR_CODES.UNSUPPORTED_FORMAT]: {
    message: "One of those photos can't be used — convert it to JPEG or PNG and try again.",
    retryable: true,
  },
  [VISITING_CARD_ERROR_CODES.MODERATION_BLOCKED]: {
    message: "This request couldn't be processed. Try a different photo or copy.",
    retryable: false,
  },
  [VISITING_CARD_ERROR_CODES.UNAUTHENTICATED]: {
    message: "Your session has expired. Please log in again.",
    retryable: false,
  },
  [VISITING_CARD_ERROR_CODES.NOT_CURATOR]: {
    message: "Only curators can use this tool.",
    retryable: false,
  },
  [VISITING_CARD_ERROR_CODES.QUOTA_EXCEEDED]: {
    message: "Daily AI image limit reached. Try again tomorrow.",
    retryable: false,
  },
  [VISITING_CARD_ERROR_CODES.BUSY]: {
    message: "The AI tool is busy right now. Please try again in a moment.",
    retryable: true,
  },
  [VISITING_CARD_ERROR_CODES.PROVIDER_FAILED]: {
    message: "Something went wrong generating that card. Please try again.",
    retryable: true,
  },
  [VISITING_CARD_ERROR_CODES.BUDGET_EXCEEDED]: {
    message: "That took too long to generate. Please try again.",
    retryable: true,
  },
};

/** Falls back to the server's own errorDetail, then to generic copy. */
export function visitingCardErrorCopy(
  errorCode: string | null | undefined,
  errorDetail?: string | null
): { message: string; retryable: boolean } {
  if (errorCode && VISITING_CARD_ERROR_COPY[errorCode]) {
    return VISITING_CARD_ERROR_COPY[errorCode];
  }
  return {
    message: errorDetail || "Couldn't generate that visiting card. Try again.",
    retryable: true,
  };
}
